import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from '@mui/icons-material';
import { Box, Button, InputAdornment, Typography } from '@mui/material';
import classnames from 'classnames';

import FormRichEditor from '@components/FormRichEditor/FormRichEditor';
import FormTextField from '@components/FormTextField/FormTextField';
import FullSizeModalSlider from '@components/FullSizeModalSlider/FullSizeModalSlider';
import ImageSlickCarousel from '@components/ImageMultiCarousel/ImageMultiCarousel';
import ViewProductPageButton from '@components/ViewProductPageButton/ViewProductPageButton';
import { TMedicationResponseMode } from '@interfaces/adminMedications';
import {
  IUserEnteredMedicationImagesResponseDto,
  MedicationFormFieldNames,
  TAdminMedicationResponseType,
} from '@interfaces/medications';
import { adminMedicationDetailsSelector } from '@store/features/adminInquiries/inquiryEntryDetails/selectors';
import {
  handleAdminMedicationConfirm,
  handleAdminMedicationNeedsMoreInfo,
} from '@store/features/adminInquiries/newMedication/actions';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import CannedResponses from '../../CannedResponses/CannedResponses';
import { appendComment } from '../../SupplementInquiryDetails/utils';

import { encodeHtmlText, getParsedText } from './utils';

import './style.scss';

const adminMedicationResponseTypes = {
  [TAdminMedicationResponseType.NeedsInfo]: TMedicationResponseMode.NeedMoreInfo,
};

const uploadImagesSliderResponsiveSettings = [
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 576,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 767,
    settings: {
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 992,
    settings: {
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 7,
    },
  },
];

interface IProps {
  className?: string;
}

const MedicationInformationForm: FC<IProps> = ({ className }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [mode, setMode] = useState(TMedicationResponseMode.ConfirmMedication);
  const [selectedImage, setSelectedImage] =
    useState<IUserEnteredMedicationImagesResponseDto | null>(null);
  const containerRef = useRef<HTMLInputElement | null>(null);

  const eventBus = useEventBus('medicationInformationForm');

  const medicationDetails = useSelector(adminMedicationDetailsSelector);

  const initialValues = useMemo(
    () => ({
      [MedicationFormFieldNames.Name]: medicationDetails?.name,
      [MedicationFormFieldNames.ProductLink]: medicationDetails?.productLink,
      [MedicationFormFieldNames.Comment]: getParsedText(medicationDetails?.comments),
    }),
    [medicationDetails],
  );

  const showNeedsInfoButton = useMemo(() => {
    if (!medicationDetails) {
      return false;
    }
    if (
      !medicationDetails.needsAdminResponse &&
      medicationDetails.adminResponseType !== TAdminMedicationResponseType.NeedsInfo
    ) {
      return false;
    }
    return mode === TMedicationResponseMode.ConfirmMedication;
  }, [medicationDetails, mode]);

  const initialSlide = useMemo(() => {
    const index = medicationDetails?.imageUrls?.findIndex(item => item.url === selectedImage?.url);
    if (typeof index === 'number' && index >= 0) {
      return index;
    }
    return 0;
  }, [selectedImage, medicationDetails?.imageUrls]);

  const closeSlider = useCallback(() => setSelectedImage(null), []);

  const validateForm = useCallback(
    (values: any) => {
      const errors: { [key: string]: boolean } = {};
      if (!values[MedicationFormFieldNames.Name]) {
        errors[MedicationFormFieldNames.Name] = true;
      }
      if (mode === TMedicationResponseMode.NeedMoreInfo) {
        if (!values[MedicationFormFieldNames.Comment]) {
          errors[MedicationFormFieldNames.Comment] = true;
        }
      }
      return errors;
    },
    [mode],
  );

  const handleConfirm = useCallback(
    (values: any) => {
      if (!medicationDetails) {
        return;
      }
      if (mode === TMedicationResponseMode.NeedMoreInfo) {
        dispatch(
          handleAdminMedicationNeedsMoreInfo({
            medicationInquiryId: medicationDetails.medicationInquiryId,
            comment: encodeHtmlText(values[MedicationFormFieldNames.Comment]),
          }),
        );
      }
      if (mode === TMedicationResponseMode.ConfirmMedication) {
        dispatch(
          handleAdminMedicationConfirm({
            medicationInquiryId: medicationDetails.medicationInquiryId,
            medicationResponseId: medicationDetails.medicationResponseId,
            comments: encodeHtmlText(values[MedicationFormFieldNames.Comment]),
          }),
        );
      }
    },
    [dispatch, medicationDetails, mode],
  );

  const handleNeedsMoreInfo = useCallback(() => {
    setMode(TMedicationResponseMode.NeedMoreInfo);
  }, []);

  const handleCancel = useCallback(() => {
    setMode(TMedicationResponseMode.ConfirmMedication);
  }, []);

  useEffect(() => {
    let mode: TMedicationResponseMode = TMedicationResponseMode.ConfirmMedication;
    if (medicationDetails?.adminResponseType) {
      mode = adminMedicationResponseTypes[
        medicationDetails?.adminResponseType
      ] as TMedicationResponseMode;
    }
    setMode(mode);
  }, [medicationDetails]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_CREATE_NEW_MEDICATION_SUCCESS, () =>
      handleToast('success', t('adminNewSupplement.medicationSubmittedSuccessfully')),
    );

    eventBus.on(EventType.ADMIN_MEDICATION_MARKED_AS_NEEDS_MORE_INFO_SUCCESS, () =>
      handleToast('success', t('adminNewSupplement.markedAsNeedsMoreInfoSuccessfully')),
    );

    return () =>
      eventBus.off([
        EventType.ADMIN_CREATE_NEW_MEDICATION_SUCCESS,
        EventType.ADMIN_MEDICATION_MARKED_AS_NEEDS_MORE_INFO_SUCCESS,
      ]);
  }, []);

  return (
    <>
      <Box className={classnames('admin-medication-information', className)}>
        <Form
          onSubmit={handleConfirm}
          initialValues={initialValues}
          validate={validateForm}
          render={({ form, handleSubmit, submitting, values, hasValidationErrors }) => (
            <form className="admin-medication-information__form" noValidate>
              <p className="subtitle-big text-bold admin-medication-information__title">
                {t('inquiryResponse.medicationInformationTitle')}
              </p>

              <Field
                component={FormTextField}
                className="admin-medication-information__row"
                label={t('addMedicationModal.medicationName')}
                name={MedicationFormFieldNames.Name}
                format={removeWhiteSpaces}
                formatOnBlur
                multiline
                required
                disabled
                maxRows={2}
              />

              <Box className="admin-medication-information__column">
                <Field
                  component={FormTextField}
                  className="admin-medication-information__column_75 admin-medication-information__link"
                  label={t('addMedicationModal.linkToProductPage')}
                  name={MedicationFormFieldNames.ProductLink}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment className="new-supplement-form__input-icon" position="start">
                        <Link color="inherit" />
                      </InputAdornment>
                    ),
                  }}
                  format={removeWhiteSpaces}
                  disabled
                  formatOnBlur
                />

                <ViewProductPageButton
                  linkToProductPage={values.productLink}
                  className="admin-medication-information__column_25"
                />
              </Box>

              {Boolean(
                Array.isArray(medicationDetails?.imageUrls) && medicationDetails?.imageUrls.length,
              ) && (
                <Box className="new-supplement-form__images-section">
                  <Typography variant="h3" className="new-supplement-form__title">
                    {t('common.photos')}
                  </Typography>
                  <Box className="new-supplement-form__images-holder" ref={containerRef}>
                    <ImageSlickCarousel
                      slidesToShow={3}
                      containerRef={containerRef}
                      responsive={uploadImagesSliderResponsiveSettings}
                    >
                      {medicationDetails?.imageUrls?.map(image => (
                        <Box
                          className="new-supplement-form__slide-holder"
                          onClick={() => setSelectedImage(image)}
                        >
                          <Box
                            width={100}
                            height={100}
                            key={image.url}
                            component="img"
                            src={image.url}
                          />
                        </Box>
                      ))}
                    </ImageSlickCarousel>
                  </Box>
                </Box>
              )}

              <Box className="admin-medication-information__comment-header">
                <Typography variant="h3" className="admin-medication-information__title">
                  {mode === TMedicationResponseMode.NeedMoreInfo
                    ? t('adminNewSupplement.needMoreInfoComments')
                    : t('inquiryResponse.comments')}
                </Typography>
                <CannedResponses
                  isMedication
                  onApply={text => {
                    form.change(
                      MedicationFormFieldNames.Comment,
                      appendComment(getParsedText(text), values[MedicationFormFieldNames.Comment]),
                    );
                  }}
                />
              </Box>

              <Field
                component={FormRichEditor}
                className="admin-medication-information__row"
                label={t('inquiryResponse.comments')}
                name={MedicationFormFieldNames.Comment}
              />

              <Box className="admin-medication-information__column">
                <Button
                  type="submit"
                  variant="contained"
                  className="admin-medication-information__button"
                  onClick={handleSubmit}
                  disabled={submitting || hasValidationErrors}
                >
                  {t('common.confirm')}
                </Button>

                {showNeedsInfoButton && (
                  <Button
                    variant="outlined"
                    className="admin-medication-information__button"
                    onClick={handleNeedsMoreInfo}
                  >
                    {t('adminNewSupplement.needMoreInfo')}
                  </Button>
                )}

                {mode === TMedicationResponseMode.NeedMoreInfo && (
                  <Button
                    variant="outlined"
                    className="admin-medication-information__button"
                    onClick={handleCancel}
                  >
                    {t('common.cancel')}
                  </Button>
                )}
              </Box>
            </form>
          )}
        />
      </Box>
      {!!selectedImage && (
        <FullSizeModalSlider
          images={medicationDetails?.imageUrls || []}
          initialSlide={initialSlide}
          handleClose={closeSlider}
        />
      )}
    </>
  );
};

export default MedicationInformationForm;
